import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { cn } from "../../../utils";
/**
 * Crossfade-and-slide container for swapping body content inside a modal.
 *
 * The outgoing layer fades out (opacity → 0, slight translateX) while the
 * incoming layer mounts at full opacity. The exiting layer is `aria-hidden`
 * so assistive tech (and testing-library queries) only see the active phase.
 *
 * The parent must establish a flex column with a defined height — the layers
 * use `position: absolute` so they overlap during the transition.
 */ export function DialogPhasePanel({ phaseKey, children, transitionMs = 280, className }) {
    const idRef = React.useRef(1);
    const [layers, setLayers] = React.useState(()=>[
            {
                id: 0,
                key: phaseKey,
                node: children,
                exiting: false
            }
        ]);
    // External sync: when phaseKey or children change, fold the update into
    // the layer stack. Same-phase updates patch the active layer's node;
    // phase changes always introduce a fresh layer so any child component
    // re-mounts on re-entry (callers that want to preserve state should keep
    // it in the parent — e.g. the SuccessorEntityPickerModal's tentative map).
    React.useEffect(()=>{
        setLayers((prev)=>{
            const active = prev.find((l)=>!l.exiting);
            if (active && active.key === phaseKey) {
                return prev.map((l)=>l.id === active.id ? {
                        ...l,
                        node: children
                    } : l);
            }
            const nextId = idRef.current++;
            return [
                ...prev.map((l)=>l.exiting ? l : {
                        ...l,
                        exiting: true
                    }),
                {
                    id: nextId,
                    key: phaseKey,
                    node: children,
                    exiting: false
                }
            ];
        });
    }, [
        phaseKey,
        children
    ]);
    // Prune exiting layers once the crossfade should be visually done.
    React.useEffect(()=>{
        if (!layers.some((l)=>l.exiting)) return;
        const t = window.setTimeout(()=>{
            setLayers((prev)=>prev.filter((l)=>!l.exiting));
        }, transitionMs + 60);
        return ()=>window.clearTimeout(t);
    }, [
        layers,
        transitionMs
    ]);
    return /*#__PURE__*/ _jsx("div", {
        className: cn("relative flex-1 min-h-0 overflow-hidden", className),
        children: layers.map((l)=>/*#__PURE__*/ _jsx("div", {
                "aria-hidden": l.exiting || undefined,
                style: {
                    position: "absolute",
                    inset: 0,
                    opacity: l.exiting ? 0 : 1,
                    transform: l.exiting ? "translateX(8px)" : "translateX(0)",
                    transition: `opacity ${transitionMs}ms var(--ease-out), transform ${transitionMs}ms var(--ease-out)`,
                    pointerEvents: l.exiting ? "none" : "auto",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0
                },
                children: l.node
            }, l.id))
    });
}

//# sourceMappingURL=DialogPhasePanel.js.map